import { useMemo, useState } from "react"
import ZebraBrowserPrintWrapper from "zebra-browser-print-wrapper"
import { ready } from "zpl-renderer-js"
import { useShallow } from "zustand/shallow"

import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useGetPalletByReferenceId } from "@/screens/Tablet/hooks/useGetPalletByReferenceId"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore.ts"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { generatePalletLabelZpl } from "@/utils/printing/generatePalletLabelZPL"

interface UsePalletLabelPrintingReturn {
  print: (referenceId: number, preview?: boolean) => Promise<boolean>
  printPreview: () => Promise<void>
  closeModal: () => void
  isModalOpen: boolean
  previewImageData: string | null
  isFetching: boolean
  isPrinting: boolean
  isGeneratingPreview: boolean
  canPrint: boolean
}

export const usePalletLabelPrinting = (): UsePalletLabelPrintingReturn => {
  const uoms = useUnitOfMeasurementsStore(useShallow((s) => s.units))
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [previewImageData, setPreviewImageData] = useState<string | null>(null)
  const [currentPallet, setCurrentPallet] = useState<GoodsReceiptPallet | null>(null)
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false)
  const [isPrintingState, setIsPrintingState] = useState(false)
  const [printingNotificationId, setPrintingNotificationId] = useState<number | null>(null)

  const { fetchPallet, isFetching } = useGetPalletByReferenceId()
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const dismissNotification = useNotificationsStore((state) => state.dismissNotification)

  const zplCode = useMemo(() => {
    if (!currentPallet) return null
    return generatePalletLabelZpl(currentPallet, uoms)
  }, [currentPallet, uoms])

  const initBrowserPrint = async (): Promise<ZebraBrowserPrintWrapper | null> => {
    try {
      const browserPrint = new ZebraBrowserPrintWrapper()

      const availablePrinters = await browserPrint.getAvailablePrinters()

      const printer = availablePrinters[0]

      if (!printer) {
        throw new Error("No printer(s) found")
      }

      browserPrint.setPrinter(printer)

      const status = await browserPrint.checkPrinterStatus()
      if (!status.isReadyToPrint) {
        throw new Error("Printer is not ready")
      }

      return browserPrint
    } catch (error) {
      console.error("Printer initialization error:", error)
      notify({
        type: NotificationType.Error,
        message: "Printer initialization error",
      })
      return null
    }
  }

  const generatePreview = async (zpl: string): Promise<string | null> => {
    setIsGeneratingPreview(true)

    try {
      const { api } = await ready
      const base64Image = await api.zplToBase64Async(zpl)
      const imageData = `data:image/png;base64,${base64Image}`
      setPreviewImageData(imageData)
      return imageData
    } catch (error) {
      console.error(error ?? "Error generating preview:", error)
      notify({
        type: NotificationType.Error,
        message: error?.toString() ?? "Failed to generate label preview",
      })
      return null
    } finally {
      setIsGeneratingPreview(false)
    }
  }

  const printDirectly = async (zpl: string): Promise<boolean> => {
    try {
      const browserPrint = await initBrowserPrint()

      if (!browserPrint) {
        return false
      }

      const startNotificationId = Date.now()
      setPrintingNotificationId(startNotificationId)
      notify({
        id: startNotificationId,
        type: NotificationType.Info,
        silent: false,
        message: "Printing QR code...",
        autoHideDuration: 10000,
      })

      await browserPrint.print(zpl)

      if (printingNotificationId) {
        dismissNotification(printingNotificationId)
      }
      dismissNotification(startNotificationId)

      notify({
        type: NotificationType.Info,
        silent: false,
        message: "Your QR codes are processing and printing. Please wait before clicking the Print button again.",
      })

      return true
    } catch (error) {
      if (printingNotificationId) {
        dismissNotification(printingNotificationId)
      }

      notify({
        type: NotificationType.Error,
        message: `Print error: ${error instanceof Error ? error.message : "Failed to print"}`,
      })
      return false
    } finally {
      setPrintingNotificationId(null)
    }
  }

  const print = async (referenceId: number, preview = false): Promise<boolean> => {
    setIsPrintingState(true)

    try {
      const pallet = await fetchPallet(referenceId)
      if (!pallet) {
        notify({
          type: NotificationType.Error,
          message: "Failed to load pallet data",
        })
        return false
      }

      setCurrentPallet(pallet)
      const zpl = generatePalletLabelZpl(pallet, uoms)

      if (preview) {
        await generatePreview(zpl)
        setIsModalOpen(true)
        return true
      } else {
        return await printDirectly(zpl)
      }
    } catch (error) {
      console.error("Error in print function:", error)
      notify({
        type: NotificationType.Error,
        message: "Failed to process print request",
      })
      return false
    } finally {
      setIsPrintingState(false)
    }
  }

  const printPreview = async () => {
    if (!zplCode) return

    setIsPrintingState(true)
    try {
      const success = await printDirectly(zplCode)
      if (success) {
        setIsModalOpen(false)
      }
    } finally {
      setIsPrintingState(false)
    }
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  return {
    print,
    printPreview,
    closeModal,
    isModalOpen,
    previewImageData,
    isFetching,
    isPrinting: isPrintingState,
    isGeneratingPreview,
    canPrint: !!zplCode,
  }
}
